import React, { useState, useEffect } from 'react';
import { navigate } from '@gatsbyjs/reach-router/';
import { useGlobal, globalizationPath, Card, Button } from '@rcg/ui-react';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { FormControl, FormHelperText, InputLabel, Select, MenuItem } from '@material-ui/core';
import PropTypes from 'prop-types';
import headerStyles from './index.style';
import { ReactComponent as CheckMarkBlue } from '../../assets/svg/checkmark_blue.svg';
import { ReactComponent as LeftArrow } from '../../assets/svg/chevron-left.svg';
import {
  ROOT_PATH,
  MAIN_PROFILE_BOOKING_ID,
  BOOKING_TOKEN,
  PROFILE_BOOKING_DATA,
  COOKIE_KEYS,
  NAVIGATION_PATHS,
  BOOKING_GROUP_TYPE,
  GA_PROFILE_BOOKINGS_LENGTH,
  SESSION_STORAGE_EXTERNAL_BOOKING,
  SESSION_STORAGE_CURRENT_BOOKING,
} from '../../commons/constants';
import { getSessionStorageItem, setSessionStorageItem, createCookie } from '../../commons/utils';
import { sendTrackingParams } from '../../commons/tracking/tracking';

const GuestAccountHeader = ({
  guestAccountData,
  setFixPosition = () => {},
  labelNoBookingAssociated,
}) => {
  const classes = headerStyles();
  let mainBooking = getSessionStorageItem(MAIN_PROFILE_BOOKING_ID);

  if (!mainBooking || mainBooking === 'undefined') {
    const currentBookingData = getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING);
    if (currentBookingData && currentBookingData.id) {
      mainBooking = currentBookingData.id;
      setSessionStorageItem(MAIN_PROFILE_BOOKING_ID, mainBooking);
    }
  }

  const [currentBooking, setCurrentBooking] = useState(mainBooking);
  const [open, setOpen] = useState(false);

  const filteredGuestAccountData = guestAccountData.filter(
    (bookingOriginal) => bookingOriginal.type !== BOOKING_GROUP_TYPE,
  );

  setSessionStorageItem(GA_PROFILE_BOOKINGS_LENGTH, filteredGuestAccountData.length);

  const {
    renderContent,
    data: { localizedDayjs, market, lang },
  } = useGlobal();

  const urlPath = globalizationPath(market, lang, ROOT_PATH);

  const handleBackToReservation = () => {
    sendTrackingParams(
      {
        eventType: 'interactions',
        interaction: `myres:backToReservationLookup`,
      },
      false,
    );
    createCookie({
      name: COOKIE_KEYS.KEEP_ALIVE,
      value: true,
      time: new Date(new Date().getTime() + 3000),
    });
    navigate(`${urlPath}/${NAVIGATION_PATHS[0].login}`);
  };

  const getProfileBookingData = (profile) => {
    const sessionStorageProfile = getSessionStorageItem(PROFILE_BOOKING_DATA);
    const token = sessionStorageProfile.filter((booking) => booking.bookingId === profile);
    return token[0].token;
  };

  const handleSelect = async (event) => {
    const bookingId = event.target.value;

    await sendTrackingParams(
      {
        eventType: 'interactions',
        interaction: `myres:switchReservation`,
      },
      false,
    );

    setCurrentBooking(bookingId);
    setSessionStorageItem(MAIN_PROFILE_BOOKING_ID, bookingId);
    setSessionStorageItem(BOOKING_TOKEN, getProfileBookingData(bookingId));
    sessionStorage.removeItem(SESSION_STORAGE_EXTERNAL_BOOKING);

    // Delay the reload by a small amount of time to ensure tracking has time to send.
    setTimeout(() => {
      // clear route location data
      navigate(`${urlPath}/${NAVIGATION_PATHS[0].dashboard}`);
      window.location.reload();
    }, 1000);
  };

  const formatedDates = (departDate, returnDate) => {
    const departurelYear = localizedDayjs(departDate).year();
    const returningYear = localizedDayjs(returnDate).year();
    if (returnDate === departDate) {
      return localizedDayjs(returnDate).format('MMM DD, YYYY');
    }
    if (returningYear !== departurelYear) {
      return `${localizedDayjs(departDate).format('MMM DD, YYYY')} - ${localizedDayjs(
        returnDate,
      ).format('MMM DD, YYYY')}`;
    }
    return `${localizedDayjs(departDate).format('MMM DD')} - ${localizedDayjs(returnDate).format(
      'MMM DD, YYYY',
    )}`;
  };

  useEffect(() => {
    if (guestAccountData.length === 0) {
      navigate(`${urlPath}/${NAVIGATION_PATHS[0].login}`);
    }
  }, []);

  return (
    <Card className={classes.mainWrapper} elevation={0}>
      <div className={classes.subHeader}>
        <div>
          <div className="bookingsButton">
            <Button onClick={handleBackToReservation} style={{ backgroundColor: 'transparent' }}>
              <LeftArrow />
              {renderContent('headerWraperBackToReservation', 'Back to Reservation Look Up')}
            </Button>
          </div>
          <div className="bookingsHeader">
            {`${renderContent('headerWraperYouHave', 'You have ')} ${guestAccountData?.length} 
            ${renderContent('headerWraperUpcomingCruises', ' upcoming Cruises')}`}
          </div>
          <div className="reservationDetails">
            {renderContent(
              'headerWraperSelectReservationNumber',
              'Select a reservation number to view the details',
            )}
          </div>
        </div>
        <div className="bookinsSelect">
          <div className={classes.selectWrapper}>
            <FormControl>
              <FormHelperText className={classes.resevationNumber}>
                {renderContent('headerWraperReservation', 'Reservation NO.')}
              </FormHelperText>
              <InputLabel id="booking-selected" shrink={false} className={classes.currentInput}>
                {currentBooking || '- -'}
              </InputLabel>
              <Select
                id="booking-selected"
                className={`select-menu ${open ? 'open-menu' : ''}`}
                value={currentBooking}
                inputProps={{ 'data-testid': 'booking-selected' }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                  },
                  getContentAnchorEl: null,
                  classes: {
                    paper: classes.customMenu,
                  },
                }}
                onChange={handleSelect}
                onMouseDown={() => {
                  setFixPosition(true);
                  setOpen(!open);
                }}
                onClick={() => setFixPosition(false)}
                IconComponent={KeyboardArrowDownIcon}>
                {guestAccountData.map(({ bookingId, sailDate, endDate, shipName }) => {
                  return (
                    <MenuItem key={bookingId} value={bookingId}>
                      <div className="check-icon">
                        <CheckMarkBlue />
                      </div>
                      <div className="list-item ">
                        <div>
                          <span>{`${bookingId} `}</span>
                          <span>&bull; </span>
                          <span className="shipName">{shipName?.toLowerCase()}</span>
                        </div>
                        <span className="dates">{`${formatedDates(sailDate, endDate)}`}</span>
                      </div>
                    </MenuItem>
                  );
                })}
                {guestAccountData.length === 0 && (
                  <MenuItem disabled value="no-bookings">
                    <div className="check-icon">
                      <CheckMarkBlue />
                    </div>
                    <div className="list-item ">
                      <div>
                        <span>{labelNoBookingAssociated}</span>
                      </div>
                    </div>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </div>
        </div>
      </div>
    </Card>
  );
};
GuestAccountHeader.propTypes = {
  guestAccountData: PropTypes.arrayOf(PropTypes.shape({})),
  setFixPosition: PropTypes.func,
  labelNoBookingAssociated: PropTypes.string,
};
export default GuestAccountHeader;
